import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/RoleModal.css';
import { useRoles } from '../hooks/useRoles';

const RoleModal = ({ show, onClose, currentRole, onRoleUpdate }) => {
  const { roles: availableRoles } = useRoles();
  const [selectedRole, setSelectedRole] = useState(currentRole || '');
  const [customRole, setCustomRole] = useState('');
  const [useCustomRole, setUseCustomRole] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setSelectedRole(currentRole || '');
      setCustomRole('');
      setUseCustomRole(false);
      setError('');
    }
  }, [show, currentRole]);

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const roleToSave = useCustomRole ? customRole.trim() : selectedRole;

      if (!roleToSave) {
        setError('Please select or enter a role');
        setLoading(false);
        return;
      }

      // If using custom role, save it to the roles API first
      if (useCustomRole && customRole.trim()) {
        try {
          await axios.post(`/api/roles/${encodeURIComponent(customRole.trim())}`, {
            label: customRole.trim()
          });
        } catch (err) {
          console.error('Failed to save custom role:', err);
          // Continue anyway - the role will still be saved to user profile
        }
      }

      // Update user's role via API
      await axios.patch('/user/profile', {
        role: roleToSave
      });

      // Notify parent component
      if (onRoleUpdate) {
        const selectedRoleObj = availableRoles.find(r => r.role === roleToSave);
        if (selectedRoleObj) {
          onRoleUpdate(selectedRoleObj);
        } else {
          // For custom roles, use the custom role text as label (properly formatted)
          const label = useCustomRole ? customRole.trim() : roleToSave;
          onRoleUpdate({ role: roleToSave, label: label });
        }
      }

      onClose();
    } catch (err) {
      console.error('Failed to update role:', err);
      setError('Failed to update role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Select Your Role</h2>
        <p className="modal-description">
          Choose your primary role within the organization. This helps customize your workflow experience.
        </p>

        <div className="role-selection">
          <label htmlFor="role-select">Select from existing roles:</label>
          <select
            id="role-select"
            value={selectedRole}
            onChange={(e) => {
              setSelectedRole(e.target.value);
              setUseCustomRole(false);
            }}
            disabled={loading || useCustomRole}
          >
            <option value="">-- Select a Role --</option>
            {availableRoles.map((roleObj) => (
              <option key={roleObj.role} value={roleObj.role}>
                {roleObj.label || roleObj.role}
              </option>
            ))}
          </select>
        </div>

        <div className="custom-role-divider">
          <span>OR</span>
        </div>

        <div className="custom-role-section">
          <label htmlFor="custom-role">Enter custom role:</label>
          <input
            id="custom-role"
            type="text"
            maxLength={100}
            value={customRole}
            onChange={(e) => {
              setCustomRole(e.target.value);
              if (e.target.value.trim()) {
                setUseCustomRole(true);
                setSelectedRole('');
              } else {
                setUseCustomRole(false);
              }
            }}
            disabled={loading}
            placeholder="e.g., Product Manager, Team Lead, etc."
          />
          <div className="character-count">
            {customRole.length}/100 characters
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-actions">
          <button onClick={onClose} disabled={loading} className="cancel-button">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || (!selectedRole && !customRole.trim())}
            className="save-button"
          >
            {loading ? 'Saving...' : 'Save Role'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleModal;
